import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card, Badge, Button, Progress } from '../components/ui';
import { 
  Plus, 
  Image, 
  Calendar, 
  Bell, 
  MapPin, 
  TrendingUp,
  Droplets,
  Sun,
  ChevronRight,
  Heart,
  Stethoscope,
  Sparkles,
  Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTattoos, setRecentTattoos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tattoosRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' }),
          fetch(`${API_URL}/api/tattoos`, { credentials: 'include' })
        ]);
        
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
        
        if (tattoosRes.ok) {
          const tattoosData = await tattoosRes.json();
          setRecentTattoos(tattoosData.slice(0, 4));
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const quickActions = [
    { icon: Plus, label: t('dashboard.addTattoo'), path: '/collection', color: 'bg-primary/20 text-primary' },
    { icon: Stethoscope, label: t('dashboard.smartCheck'), path: '/smart-check', color: 'bg-secondary/20 text-secondary', highlight: true },
    { icon: MapPin, label: t('dashboard.addPlan'), path: '/body-map', color: 'bg-green-500/20 text-green-400' },
    { icon: Sparkles, label: t('dashboard.arPreview'), path: '/ar-preview', color: 'bg-purple-500/20 text-purple-400', highlight: true },
  ];

  const careActions = [
    { icon: Droplets, label: t('dashboard.moisturize'), time: '10:00', done: true },
    { icon: Sun, label: t('dashboard.sunscreen'), time: '14:00', done: false },
    { icon: Droplets, label: t('dashboard.moisturize'), time: '22:00', done: false },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="font-heading text-3xl md:text-4xl mb-2" data-testid="dashboard-welcome">
            {t('dashboard.greeting')}, <span className="text-primary">{user?.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-muted-foreground">
            {t('dashboard.subtitle')}
          </p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
        >
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <motion.div key={index} variants={itemVariants}>
                <Link to={action.path}>
                  <Card className="p-6 text-center hover:border-primary/50 cursor-pointer" data-testid={`quick-action-${index}`}>
                    <div className={`w-12 h-12 rounded-sm ${action.color} flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="text-sm font-medium">{action.label}</span>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Left 2 Columns */}
          <div className="lg:col-span-2 space-y-8">
            {/* Stats Cards */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {[
                { label: t('dashboard.totalTattoos'), value: stats?.total_tattoos || 0, icon: Image },
                { label: t('dashboard.healing'), value: stats?.healing_tattoos || 0, icon: Heart },
                { label: t('dashboard.plans'), value: stats?.total_plans || 0, icon: MapPin },
                { label: t('dashboard.reminders'), value: stats?.active_reminders || 0, icon: Bell },
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div key={index} variants={itemVariants}>
                    <Card className="p-4" data-testid={`stat-card-${index}`}>
                      <div className="flex items-center justify-between mb-2">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <span className="font-heading text-2xl text-primary">{stat.value}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Recent Tattoos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl">{t('dashboard.recentTattoos')}</h2>
                <Link to="/collection" className="text-sm text-primary hover:underline flex items-center gap-1">
                  {t('dashboard.viewAll')} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              {recentTattoos.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {recentTattoos.map((tattoo, index) => (
                    <Card key={tattoo.tattoo_id} className="overflow-hidden" data-testid={`recent-tattoo-${index}`}>
                      <div className="h-32 bg-muted relative">
                        {tattoo.photos?.[0]?.url ? (
                          <img 
                            src={tattoo.photos[0].url} 
                            alt={tattoo.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        <Badge 
                          variant={tattoo.healing_status === 'healed' ? 'success' : 'warning'}
                          className="absolute top-2 right-2"
                        >
                          {tattoo.healing_status === 'healed' ? t('dashboard.healed') : t('dashboard.healing')}
                        </Badge>
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-lg mb-1">{tattoo.name}</h3>
                        <p className="text-sm text-muted-foreground">{tattoo.body_location}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="p-8 text-center">
                  <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">{t('dashboard.noTattoos')}</p>
                  <Link to="/collection">
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('dashboard.addFirst')}
                    </Button>
                  </Link>
                </Card>
              )}
            </motion.div>

            {/* Healing Progress */}
            {stats?.healing_tattoos > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-heading text-xl mb-4">{t('dashboard.healingTracker')}</h2>
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium">{t('dashboard.activeHealing')}</h3>
                      <p className="text-sm text-muted-foreground">{stats.healing_tattoos} {t('dashboard.tattoosHealing')}</p>
                    </div>
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <Progress value={65} className="mb-2" />
                  <p className="text-xs text-muted-foreground">{t('dashboard.avgHealing')}: 65%</p>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-8">
            {/* Today's Care */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-heading text-xl mb-4">{t('dashboard.todayCare')}</h2>
              <Card className="p-4">
                <div className="space-y-3">
                  {careActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <div 
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-sm ${
                          action.done ? 'bg-primary/10' : 'bg-muted'
                        }`}
                        data-testid={`care-action-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${action.done ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div>
                            <p className={`text-sm font-medium ${action.done ? 'line-through text-muted-foreground' : ''}`}>
                              {action.label}
                            </p>
                            <p className="text-xs text-muted-foreground">{action.time}</p>
                          </div>
                        </div>
                        {action.done && (
                          <Badge variant="success" className="text-xs">{t('dashboard.completed')}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Link to="/care" className="block mt-4">
                  <Button variant="outline" size="sm" className="w-full">
                    {t('dashboard.allCareRoutine')}
                  </Button>
                </Link>
              </Card>
            </motion.div>

            {/* Quick Tips */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="font-heading text-xl mb-4">{t('dashboard.dailyTip')}</h2>
              <Card className="p-4 border-primary/30">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Sun className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">{t('dashboard.sunProtection')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.sunProtectionTip')}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Subscription CTA */}
            {user?.subscription_plan === 'free' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="p-6 bg-gradient-to-br from-secondary/20 to-primary/10 border-secondary/30">
                  <h3 className="font-heading text-lg mb-2">Premium'a Geç</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Sınırsız dövme kaydı, AI renk analizi ve daha fazlası!
                  </p>
                  <Link to="/pricing">
                    <Button size="sm" className="w-full">
                      Planları İncele
                    </Button>
                  </Link>
                </Card>
              </motion.div>
            )}

            {/* AI Features Promo */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="p-4 border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
                <h3 className="font-heading text-sm mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  AI Özellikleri
                </h3>
                <div className="space-y-2">
                  <Link 
                    to="/smart-check"
                    className="flex items-center gap-3 p-2 rounded-sm hover:bg-primary/10 transition-colors"
                  >
                    <Stethoscope className="w-5 h-5 text-secondary" />
                    <div>
                      <p className="text-sm font-medium">Smart Check</p>
                      <p className="text-xs text-muted-foreground">AI sağlık analizi</p>
                    </div>
                  </Link>
                  <Link 
                    to="/ar-preview"
                    className="flex items-center gap-3 p-2 rounded-sm hover:bg-primary/10 transition-colors"
                  >
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-sm font-medium">AR Önizleme</p>
                      <p className="text-xs text-muted-foreground">Dövme tasarımını dene</p>
                    </div>
                  </Link>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
